import { z } from "zod";
import { normalizeApiBaseUrl, type FetchLike } from "./shared";

export const deleteChannelBodySchema = z.object({
  workspace_id: z.string().uuid(),
});

export const deleteWorkspaceBodySchema = z.object({
  confirm_name: z.string().min(1).max(200),
});

export const deleteChannelSuccessSchema = z.object({
  ok: z.literal(true),
  deleted_channel_id: z.string(),
});

export const deleteWorkspaceSuccessSchema = z.object({
  ok: z.literal(true),
  deleted_workspace_id: z.string(),
});

export const deleteApiErrorSchema = z.object({
  error: z.string(),
  details: z.unknown().optional(),
});

export type DeleteChannelBody = z.infer<typeof deleteChannelBodySchema>;
export type DeleteWorkspaceBody = z.infer<typeof deleteWorkspaceBodySchema>;
export type DeleteChannelSuccess = z.infer<typeof deleteChannelSuccessSchema>;
export type DeleteWorkspaceSuccess = z.infer<
  typeof deleteWorkspaceSuccessSchema
>;

export function deleteChannelPath(channelId: string): string {
  return `/api/channels/${channelId}/delete`;
}

export function deleteWorkspacePath(workspaceId: string): string {
  return `/api/workspaces/${workspaceId}/delete`;
}

export class DeleteApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "DeleteApiError";
    this.status = status;
    this.details = details;
  }
}

type DeleteRequestContext = {
  apiBaseUrl: string;
  accessToken: string;
  fetchImpl?: FetchLike;
};

type DeleteChannelRequestInput = DeleteRequestContext & {
  channelId: string;
  workspaceId: string;
};

type DeleteWorkspaceRequestInput = DeleteRequestContext & {
  workspaceId: string;
  confirmName: string;
};

async function parseDeleteError(
  response: Response,
  fallbackMessage: string,
): Promise<DeleteApiError> {
  const payload = await response.json().catch(() => null);
  const parsed = deleteApiErrorSchema.safeParse(payload);
  if (parsed.success) {
    return new DeleteApiError(
      parsed.data.error,
      response.status,
      parsed.data.details,
    );
  }
  return new DeleteApiError(fallbackMessage, response.status);
}

export async function deleteChannelRequest({
  apiBaseUrl,
  accessToken,
  channelId,
  workspaceId,
  fetchImpl,
}: DeleteChannelRequestInput): Promise<DeleteChannelSuccess> {
  const body = deleteChannelBodySchema.parse({ workspace_id: workspaceId });
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${deleteChannelPath(channelId)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseDeleteError(response, "Failed to delete channel");
  }

  const payload = await response.json().catch(() => null);
  const parsed = deleteChannelSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new DeleteApiError("Invalid channel delete response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}

export async function deleteWorkspaceRequest({
  apiBaseUrl,
  accessToken,
  workspaceId,
  confirmName,
  fetchImpl,
}: DeleteWorkspaceRequestInput): Promise<DeleteWorkspaceSuccess> {
  const body = deleteWorkspaceBodySchema.parse({ confirm_name: confirmName });
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${deleteWorkspacePath(workspaceId)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseDeleteError(response, "Failed to delete workspace");
  }

  const payload = await response.json().catch(() => null);
  const parsed = deleteWorkspaceSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new DeleteApiError("Invalid workspace delete response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}
